import type { GetContentResModel } from '@boostbrothers/web-api/web-gateway-api';
import { Button, SimpleAlertModal } from '@boostbrothers/web-ui/web';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import IconArrowLeft from '~assets/icons/icon-arrow-left.svg?react';
import useModal from '~hooks/useModal';
import useViewTransition from '~hooks/useViewTransition';
import { isEmpty, isEqual, normalizeEmptyParagraphs } from '~utils/editor';
import useContentMutation from '../hooks/useContentMutation';

interface HeaderProps {
  contentId: string | null;
  title: string;
  document: string;
  previewText: string;
  thumbnail: {
    url: string;
    fileName: string;
  };
  savedContent?: GetContentResModel;
  fromPA: boolean;
  handleTemporarySave: () => void;
}

const Header = ({
  contentId,
  title,
  document,
  previewText,
  thumbnail,
  savedContent,
  fromPA,
  handleTemporarySave,
}: HeaderProps) => {
  const { navigateWithTransition } = useViewTransition();
  const { openModal, closeModal } = useModal();
  const { mutateUpdateContent, mutateCreateContent } = useContentMutation({
    fromPA,
    handleTemporarySave,
  });

  const isModified = useMemo(() => {
    if (!savedContent) {
      return (
        !isEmpty(document) ||
        title.trim() ||
        previewText.trim() ||
        thumbnail.url.trim()
      );
    }

    return (
      !isEqual(document, savedContent.content) ||
      title !== savedContent.title ||
      previewText !== (savedContent.previewText || '') ||
      thumbnail.url !== (savedContent.thumbnailUrl || '')
    );
  }, [title, document, previewText, thumbnail.url, savedContent]);

  const isExitModalOpenRef = useRef(false);

  const openLeaveConfirmModal = useCallback(() => {
    if (isExitModalOpenRef.current) return;
    isExitModalOpenRef.current = true;

    openModal({
      modalContent: (
        <SimpleAlertModal
          title="변경 사항을 저장하지 않고 나가시겠습니까?"
          description="페이지를 벗어나면 변경하신 내용이 모두 사라집니다."
          closeText="계속 작성하기"
          confirmText="나가기"
          closeAction={() => {
            isExitModalOpenRef.current = false;
            closeModal();
          }}
          confirmAction={() => {
            isExitModalOpenRef.current = false;
            closeModal();
            navigateWithTransition('/');
          }}
        />
      ),
    });
  }, [closeModal, navigateWithTransition, openModal]);

  const onClickBack = () => {
    if (isModified) {
      openLeaveConfirmModal();
      return;
    }

    navigateWithTransition('/');
  };

  const onClickSave = () => {
    const normalizedDocument = normalizeEmptyParagraphs(document);

    if (contentId && contentId !== '') {
      mutateUpdateContent({
        params: { contentId },
        payload: {
          title,
          content: normalizedDocument,
          previewText,
          thumbnailUrl: thumbnail.url,
          thumbnailFileName: thumbnail.fileName,
        },
      });
    } else {
      mutateCreateContent({
        payload: {
          title,
          content: normalizedDocument,
          previewText,
          thumbnailUrl: thumbnail.url,
          thumbnailFileName: thumbnail.fileName,
        },
      });
    }
  };

  useEffect(() => {
    // 브라우저 탭 닫기/새로고침은 커스텀 모달을 띄울 수 없어 네이티브 경고만 가능합니다.
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isModified) return;
      event.preventDefault();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [isModified]);

  useEffect(() => {
    // 브라우저 뒤로가기(History back) 시: 1회 트랩용 state를 쌓아두고 popstate에서 모달 처리
    if (typeof window === 'undefined') return;

    const TRAP_STATE_KEY = 'ddocdocEditLeaveTrap';

    const pushTrapState = () => {
      // 동일한 trap을 중복으로 쌓지 않기 위해 현재 state를 확인합니다.
      const current = window.history.state as Record<string, unknown> | null;
      if (current?.[TRAP_STATE_KEY]) return;
      window.history.pushState(
        { ...(current ?? {}), [TRAP_STATE_KEY]: true },
        ''
      );
    };

    if (isModified) {
      pushTrapState();
    }

    const onPopState = () => {
      // 수정사항이 없으면 즉시 실제 back을 진행합니다.
      if (!isModified) {
        window.history.back();
        return;
      }

      // 수정사항이 있으면 "edit(trap)" 상태를 복구하고, 이탈 확인 모달을 띄웁니다.
      pushTrapState();
      openLeaveConfirmModal();
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [isModified, openLeaveConfirmModal]);

  return (
    <header
      className={`flex items-center rounded-xl bg-white p-4 ${
        fromPA ? 'justify-end' : 'justify-between'
      }`}
    >
      {!fromPA && (
        <button
          onClick={onClickBack}
          className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-[#F5F7F9]"
        >
          <IconArrowLeft className="h-6 w-6" />
        </button>
      )}

      <Button
        label={'저장하기'}
        colorType="primary"
        size="base"
        disabled={!title.trim() || isEmpty(document) || !isModified}
        onClick={onClickSave}
      />
    </header>
  );
};

export default Header;
